import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnDestroy,
    Output,
    TemplateRef,
} from '@angular/core';
import {skipWhilePreviousCall} from 'app/core/helpers/decorators';
import {transitionEnd} from 'app/core/helpers/dom';
import {PositionDirection} from 'app/core/interfaces/overlay.interface';
import {DomService} from 'app/core/services/dom.service';
import {OverlayService} from 'app/core/services/overlay.service';
import {OverlayElementEditor} from 'app/shared/classes/overlay-element-editor';
import {DropDownModernContentComponent} from 'app/shared/components/drop-down/drop-down-modern-content/drop-down-modern-content.component';
import {DropDownComponent} from 'app/shared/components/drop-down/drop-down.component';
import {fromEvent, Subscription} from 'rxjs';
import {debounceTime, first} from 'rxjs/operators';

@Directive({
    selector: '[dropDown]',
})
export class DropDownDirective implements OnDestroy {
    @Input() optionsTemplate: TemplateRef<any>;
    @Input() manualToggle = false;
    @Input() disabled = false;
    @Input() closeOnResize: boolean;

    modern = true;

    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() open = new EventEmitter<this>();
    @Output() optionsVisibility = new EventEmitter<boolean>();
    @Output() contentRender = new EventEmitter<OverlayElementEditor>();
    @Output() contentHide = new EventEmitter<OverlayElementEditor>();
    optionsVisible = false;
    resizeSub = Subscription.EMPTY;
    private contentId: string;
    private contentElement: HTMLElement;
    private removeDocClickListener: () => void;

    constructor(
        private cd: ChangeDetectorRef,
        private $el: ElementRef<HTMLElement>,
        private domService: DomService,
        private overlayService: OverlayService,
        private ngZone: NgZone
    ) {}

    ngOnDestroy() {
        this.safeRemoveDocClickListener();
        if (this.optionsVisible && this.contentElement) {
            this.overlayService.hideTemplate(this.contentId).then(editor => editor.removeElement());
        }
    }

    @skipWhilePreviousCall()
    async toggleContent(value?: boolean, closeDropDown?: boolean) {
        this.optionsVisible = value === undefined ? !this.optionsVisible : value;
        if (this.optionsVisible) {
            await this.openDropDownModernContent.bind(this)();
            this.removeDocClickListener = this.domService.onDocClick(e => this.onDocClick(e));

            if (this.closeOnResize) {
                this.addResizeCloseDropdownListener();
            }
        } else {
            this.safeRemoveDocClickListener();

            if (!closeDropDown) {
                await this.closeDropDownModernContent.bind(this)();
            }
        }
        this.optionsVisibility.emit(this.optionsVisible);
        this.open.emit(this.optionsVisible ? this : null);
    }

    onDocClick(event: Event) {
        if (!this.contentElement) {
            this.cd.markForCheck();
            return;
        }
        if (
            !this.contentElement.contains(event.target as Node) ||
            // @ts-ignore
            String(event.target.tagName).toLowerCase() === 'drop-down-modern-content'
        ) {
            this.toggleContent(false);
            this.cd.markForCheck();
        }
    }

    @HostListener('click', ['$event'])
    onClick(e: MouseEvent) {
        if (this.manualToggle) {
            return;
        }
        return this.onToggleBtnClick(e);
    }

    onToggleBtnClick = (event: MouseEvent) => {
        event.stopPropagation();
        if (!this.disabled) {
            this.toggleContent();
        }
    };

    addResizeCloseDropdownListener(): void {
        fromEvent(window, 'resize')
            .pipe(first())
            .subscribe(() => this.toggleContent(false));
    }

    /* istanbul ignore next */
    public updateHeight() {
        if (this.contentElement) {
            let wrapper = this.contentElement.querySelector('.drop-down-content-wrapper') as HTMLElement;
            let borderWidth = parseInt(getComputedStyle(this.contentElement).getPropertyValue('--border-width'), 10);

            let copyContainer = document.createElement('div');
            let copy = wrapper.cloneNode(true) as HTMLDivElement;

            copyContainer.style.cssText = 'visibility: hidden; position: fixed;';
            copy.style.setProperty('height', 'auto');

            copyContainer.append(copy);
            wrapper.parentNode.append(copyContainer);
            requestAnimationFrame(() => {
                wrapper.parentElement.style.setProperty('--height-expanded', `${copy.getBoundingClientRect().height + 2 * borderWidth}px`);
                copyContainer.remove();
            });
        }
    }

    private safeRemoveDocClickListener() {
        if (typeof this.removeDocClickListener === 'function') {
            this.removeDocClickListener();
            this.removeDocClickListener = null;
        }
    }

    private renderContentComponent = async component => {
        let content = await this.overlayService.renderTemplate(component, {
            template: this.optionsTemplate,
        });
        this.contentId = content.id;
        this.contentElement = content.getElement();
        content.copyCustomProperties(this.$el.nativeElement);
        content.copyStyles(this.$el.nativeElement, ['font-size']);
        await content.tick();
        this.contentRender.emit(content);
        return content;
    };

    private async openDropDownModernContent(): Promise<void> {
        let editor = await this.renderContentComponent(DropDownModernContentComponent);
        await editor.tick();
        this.contentRender.emit(editor);
        let dropDownContent = DropDownComponent.getContentElement(editor, 'drop-down-modern-content');
        let {collapsedRect, expandedRect} = this.getRects(editor);
        let directionG: PositionDirection;
        if (!editor.positionSpecified) {
            let {coordinates, direction} = DropDownComponent.getContentPosition(collapsedRect, expandedRect);
            directionG = direction;
            coordinates.y += collapsedRect.height;
            dropDownContent.style.setProperty('transition', 'none');
            dropDownContent.classList.add(`horizontal-${direction.horizontal}`);
            dropDownContent.classList.add(`vertical-${direction.vertical}`);
            editor.setPosition(coordinates, direction);
            await editor.tick();
        }
        let fixedParent = editor.hasFixedParent(this.$el.nativeElement);
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        if (window.innerWidth < 768 || window.innerHeight < 600) {
            // show full screen
            editor.setPosition(
                {
                    x: Math.max((window.innerWidth - expandedRect.width) / 2, 0),
                    y: Math.max((window.innerHeight - expandedRect.height) / 2, 0),
                },
                {
                    vertical: 'down',
                    horizontal: 'right',
                }
            );
            editor.showElement(true, '100');
            this.resizeSub = fromEvent(window, 'resize')
                .pipe(debounceTime(10))
                .subscribe(() => {
                    let {expandedRect: updatedRect} = this.getRects(editor);
                    editor.setPosition(
                        {
                            x: Math.max((window.innerWidth - updatedRect.width) / 2, 0),
                            y: Math.max((window.innerHeight - updatedRect.height) / 2, 0),
                        },
                        {
                            vertical: 'down',
                            horizontal: 'right',
                        }
                    );
                    editor.showElement(true, '100');
                });
        } else {
            editor.showElement(!!fixedParent, fixedParent ? getComputedStyle(fixedParent).zIndex : null);
        }
        dropDownContent.style.removeProperty('transition');
        await editor.tick();
        dropDownContent.classList.add('expanding');
        if (directionG.vertical === 'up') {
            const arrow = dropDownContent.querySelector('.drop-down-arrow') as SVGAElement;
            if (arrow) {
                arrow.style.display = 'none';
            }
            dropDownContent.style.opacity = '1';
        } else {
            await transitionEnd(dropDownContent, ['opacity']);
        }
        dropDownContent.classList.add('expanded');
    }

    private async closeDropDownModernContent(): Promise<void> {
        this.resizeSub.unsubscribe();
        let editor = await this.overlayService.hideTemplate(this.contentId);
        this.contentHide.emit(editor);
        if (editor.getElement()) {
            let dropDownContent = DropDownComponent.getContentElement(editor, 'drop-down-modern-content');
            dropDownContent.classList.remove('expanded');
            await editor.tick();
            dropDownContent.classList.remove('expanding');
            // dropDownContent.style.opacity = '0';
            if (dropDownContent.style.opacity === '1') {
                dropDownContent.style.opacity = '0';
            } else {
                await transitionEnd(dropDownContent, ['opacity']);
            }
            editor.removeElement();
        }
        this.contentElement = null;
    }

    private getRects(editor: OverlayElementEditor) {
        let dropDownContent = editor.getElement().firstElementChild as HTMLElement;
        let collapsedRect = this.$el.nativeElement.getBoundingClientRect() as DOMRect;
        let expandedRect = dropDownContent.getBoundingClientRect() as DOMRect;
        return {
            collapsedRect,
            expandedRect,
        };
    }

    isOutsideMode(): boolean {
        return true;
    }
}
